import React, { useCallback, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';

import {
  VaAlert,
  VaButton,
  VaIcon,
  VaTextarea,
  VaTelephone,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { apiRequest } from '@department-of-veterans-affairs/platform-utilities/api';
import { focusElement } from '@department-of-veterans-affairs/platform-utilities/ui';

import BreadCrumbs from '../components/BreadCrumbs';
import EnvironmentBanner from '../components/EnvironmentBanner';
import FileUpload from '../components/FileUpload';
import Loader from '../components/Loader';
import NeedHelpFooter from '../components/NeedHelpFooter';
import { formatDate, getFiles, getVAStatusFromCRM } from '../config/helpers';
import { getMockTestingFlagForAPI, URL } from '../constants';
import manifest from '../manifest.json';
import { ENDPOINTS, getInquiry } from '../utils/api';
import { mockInquiryResponse } from '../utils/mockData';
import { askVAAttachmentStorage } from '../utils/StorageAdapter';
import { DownloadAttachment } from '../components/DownloadAttachment';
import { buildConversation } from '../utils/conversation';

const getReplySubHeader = messageType => {
  if (!messageType) return 'No messageType';
  if (messageType === 'ResponseFromVA') return 'Response from VA';
  if (messageType === 'ReplyToVA') return 'Reply to VA';
  // Split the string at capital letters and join with spaces
  return messageType.split(/(?=[A-Z])/).join(' ');
};

function ConversationPage({ router }) {
  const [error, setError] = useState(false);
  const [replyTextError, setReplyTextError] = useState('');
  const [sendReply, setSendReply] = useState({ reply: '', files: [] });
  const [loading, setLoading] = useState(true);
  const [inquiryData, setInquiryData] = useState(null);

  const inquiryId = router.params?.id;

  const postApiData = useCallback(
    async url => {
      const files = await askVAAttachmentStorage.get('attachments');
      const transformedResponse = {
        ...sendReply,
        files: getFiles(files),
      };
      const options = {
        method: 'POST',
        body: JSON.stringify(transformedResponse),
        headers: {
          'Content-Type': 'application/json',
        },
      };

      setLoading(true);

      if (getMockTestingFlagForAPI()) {
        // Simulate API delay
        return new Promise(resolve => {
          setTimeout(() => {
            setLoading(false);
            askVAAttachmentStorage.clear();
            resolve(mockInquiryResponse);
            router.push('/response-sent');
          }, 500);
        });
      }

      return apiRequest(url, options)
        .then(() => {
          setLoading(false);
          askVAAttachmentStorage.clear();
          router.push('/response-sent');
        })
        .catch(() => {
          setLoading(false);
          askVAAttachmentStorage.clear();
          setError(true);
        });
    },
    [router, sendReply],
  );

  const handleInputChange = event => {
    setSendReply({ ...sendReply, reply: event.target.value });
    if (replyTextError) setReplyTextError('');
  };

  const handleSubmitReply = event => {
    event.preventDefault();
    if (/\S/.test(sendReply.reply)) {
      postApiData(`${ENDPOINTS.inquiries}/${inquiryId}${URL.SEND_REPLY}`);
    } else {
      setReplyTextError('Enter a valid reply.');
    }
  };

  const getApiData = useCallback(() => {
    setLoading(true);
    setError(false);

    if (getMockTestingFlagForAPI() && !window.Cypress) {
      // Simulate API delay
      return new Promise(resolve => {
        setTimeout(() => {
          setInquiryData(mockInquiryResponse.data);
          setLoading(false);
          resolve(mockInquiryResponse);
        }, 500);
      });
    }
    return getInquiry(inquiryId)
      .then(res => {
        setInquiryData(res.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }, [inquiryId]);

  useEffect(() => {
    if (inquiryId) getApiData();
  }, [inquiryId, getApiData]);

  useEffect(() => {
    focusElement('h1');
  }, [loading]);

  if (error) {
    return (
      <VaAlert status="error" className="vads-u-margin-y--4">
        <h2
          slot="headline"
          className="vads-u-font-size--h3 vads-u-margin-y--0 vads-u-font-size--lg"
        >
          We’ve run into a problem
        </h2>
        <p className="vads-u-font-size--base">
          We’re sorry. Something went wrong on our end. Try again later or call
          us at <VaTelephone contact="800-698-2411" /> (
          <VaTelephone contact="711" tty />
          ). We’re here 24/7.
        </p>
      </VaAlert>
    );
  }

  if (loading) return <Loader />;

  if (!inquiryData) {
    return (
      <VaAlert status="info" className="vads-u-margin-y--4">
        No inquiry data available.
      </VaAlert>
    );
  }

  const conversation = buildConversation(inquiryData.attributes);

  return (
    <div className="row vads-u-padding-x--1">
      <EnvironmentBanner />
      <BreadCrumbs currentPath={window.location.pathname} />
      <div
        className="usa-width-two-thirds medium-8 columns vads-u-padding--0"
        data-dd-privacy="hidden"
      >
        <h1 className="vads-u-margin-bottom--2p5">Question details</h1>
        <dl className="vads-u-margin-top--0 vads-u-margin-bottom--3">
          <div className="vads-u-margin-bottom--1p5">
            <dt className="sr-only">Status</dt>
            <dd>
              <span className="usa-label vads-u-font-weight--normal vads-u-font-family--sans">
                {getVAStatusFromCRM(inquiryData.attributes.status)}
              </span>
            </dd>
          </div>
          <div className="vads-u-margin-bottom--1">
            <dt className="vads-u-display--inline vads-u-font-weight--bold">
              Submitted:
            </dt>
            <dd className="vads-u-display--inline">
              {' '}
              {formatDate(inquiryData.attributes.createdOn)}
            </dd>
          </div>
          <div className="vads-u-margin-bottom--1">
            <dt className="vads-u-display--inline vads-u-font-weight--bold">
              Last updated:
            </dt>
            <dd className="vads-u-display--inline">
              {' '}
              {formatDate(inquiryData.attributes.lastUpdate)}
            </dd>
          </div>
          <div className="vads-u-margin-bottom--1">
            <dt className="vads-u-display--inline vads-u-font-weight--bold">
              Category:
            </dt>
            <dd className="vads-u-display--inline">
              {' '}
              {inquiryData.attributes.categoryName}
            </dd>
          </div>
          <div>
            <dt className="vads-u-display--inline vads-u-font-weight--bold">
              Reference number:
            </dt>
            <dd className="vads-u-display--inline">
              {' '}
              {inquiryData.attributes.inquiryNumber}
            </dd>
          </div>
        </dl>

        <div className="vads-l-row vads-u-justify-content--space-between vads-u-align-items--baseline">
          <h2 className="vads-u-margin-y--2">Your conversation</h2>
          {/* There's no built-in button option for this specific icon use case yet */}
          {/* eslint-disable-next-line @department-of-veterans-affairs/prefer-button-component */}
          <button
            className="vads-u-display--flex vads-u-flex-direction--row vads-u-align-items--center va-button-link vads-u-text-decoration--none"
            type="button"
            onClick={() => window.print()}
            aria-label="Print conversation"
          >
            <span className="vads-u-color--primary">
              <VaIcon
                size={3}
                icon="print"
                className="vads-u-margin-right--1"
              />
            </span>
            <span className="vads-u-font-weight--bold vads-u-color--primary vads-u-font-size--h3">
              PRINT
            </span>
          </button>
        </div>

        <div className="vads-u-border-color--gray-lighter vads-u-margin-top--3 vads-u-padding-top--3 vads-u-border-top--1px vads-u-margin-bottom--4 vads-u-padding-bottom--6 vads-u-border-bottom--1px">
          {conversation.length === 0 ? (
            <div className="no-messages">
              <p className="vads-u-background-color--gray-light-alt empty-message">
                There are no messages in your inbox
              </p>
            </div>
          ) : (
            <va-accordion openSingle={conversation.length === 1}>
              {conversation.map(correspondence => {
                const {
                  id,
                  attributes: { messageType, createdOn, description = '' },
                } = correspondence;
                // Validate attachments before attempting to download them
                const attachments = (
                  correspondence.attributes.attachments || []
                ).filter(file => file?.id && file?.name);

                return (
                  <va-accordion-item
                    bordered
                    key={id}
                    header={getReplySubHeader(messageType)}
                    subHeader={createdOn}
                    level={3}
                  >
                    {attachments.length > 0 && (
                      <VaIcon
                        icon="attach_file"
                        size={2}
                        alt="Attachment icon"
                        aria-hidden="true"
                        slot="subheader-icon"
                      />
                    )}
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(description),
                      }}
                      className="vads-u-margin--0 vads-u-margin-bottom--3"
                    />
                    {attachments.length > 0 && (
                      <div>
                        <h4>Attachments</h4>
                        <ul className="conversation-attachments">
                          {attachments.map(file => (
                            <li key={file.id}>
                              <DownloadAttachment {...{ file }} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </va-accordion-item>
                );
              })}
            </va-accordion>
          )}
        </div>

        {inquiryData.attributes?.allowReplies ? (
          <>
            <h2 className="vads-u-margin-bottom--0 vads-u-margin-top--4">
              Send a reply
            </h2>
            <form
              className="vads-u-margin-bottom--5 vads-u-margin-top--0"
              onSubmit={handleSubmitReply}
            >
              <fieldset>
                <VaTextarea
                  className="resize-y"
                  error={replyTextError}
                  label="Your message"
                  name="reply message"
                  onInput={handleInputChange}
                  value={sendReply.reply}
                  required
                />

                {inquiryData.attributes?.allowAttachments && <FileUpload />}

                <VaButton
                  onClick={handleSubmitReply}
                  primary
                  className="vads-u-margin-top--3"
                  text="Send"
                  aria-label="Submit reply"
                />
              </fieldset>
            </form>
          </>
        ) : (
          <div className="vads-u-margin-top--6 vads-u-margin-bottom--7">
            <va-alert
              close-btn-aria-label="Close notification"
              status="info"
              visible
            >
              <h3 id="track-your-status-on-mobile" slot="headline">
                Send a reply
              </h3>
              <p className="vads-u-margin-y--0">
                To send a reply,{' '}
                <a
                  className="usa-link"
                  href={`${manifest.rootUrl}/introduction`}
                >
                  ask a new question
                </a>
                .
              </p>
            </va-alert>
          </div>
        )}
        <NeedHelpFooter />
      </div>
    </div>
  );
}

ConversationPage.propTypes = {
  router: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default withRouter(ConversationPage);
